import { safeFormat } from '@/v2/utils/convert'
import { tokenCodec } from '../reschedule/plainTemplate'

export const HEALTH_HISTORY_DONE = 'completed'

// The variables the cancellation notice renders with. The health history one
// is a whole sentence with the link, and nothing once the form is completed,
// so the notice never asks for a form the patient already sent.
export const CANCELLATION_TOKENS = [
  { label: 'First name', variable: 'patient.first_name', sample: 'Ana' },
  {
    label: 'Appointment',
    variable: 'appointment.type',
    sample: 'New Patient Consult',
  },
  { label: 'Day', variable: 'appointment.day', sample: 'Fri, Sep 4' },
  { label: 'Time', variable: 'appointment.time', sample: '10:30 AM' },
  {
    label: 'Doctor',
    variable: 'appointment.doctor_name',
    sample: 'Dr. Nayson Niaraki',
  },
  { label: 'Clinic name', variable: 'clinic.name', sample: 'Smilebar' },
  {
    label: 'Reason',
    variable: 'cancellation.reason',
    sample: "we didn't receive your confirmation or your health history",
  },
  {
    label: 'Health history link',
    variable: 'cancellation.health_history_request',
    sample:
      'Please complete your health history: smilebar.clearworks.com/v2/health-history-form/…',
  },
  {
    label: 'Booking link',
    variable: 'booking_page.url',
    sample: 'smilebar.clearworks.com/book',
  },
]

export const REASONS = [
  { value: 'not_confirmed', key: 'notConfirmed', label: "Didn't confirm" },
  {
    value: 'health_history',
    key: 'healthHistory',
    label: 'Health history not completed',
  },
  {
    value: 'both',
    key: 'both',
    label: "Didn't confirm and health history not completed",
  },
]

const { toLiquid, fromLiquid } = tokenCodec(CANCELLATION_TOKENS)

export const compile = text => toLiquid(text)

// Plain text, or null when the template has logic of its own and has to be
// edited as code.
export const parse = template => {
  if ((template || '').includes('{%')) return null

  const plain = fromLiquid(template || '')
  if (plain === null) return null

  return compile(plain) === (template || '') ? plain : null
}

// The plain text with each token replaced by its value. An empty value leaves
// no double space or blank line behind, as the server render does.
export const fill = (text, values) =>
  (text || '')
    .replace(/\[([^\]]+)\]/g, (match, label) =>
      label in values ? values[label] : match
    )
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ +\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

// The booking link falls back to its sample only when the clinic has no
// booking page to link to.
export const sampleValues = (reason, { bookingUrl } = {}) =>
  Object.fromEntries(
    CANCELLATION_TOKENS.map(({ label, sample }) => {
      if (label === 'Reason' && reason) return [label, reason]
      if (label === 'Booking link' && bookingUrl) return [label, bookingUrl]
      return [label, sample]
    })
  )

// The saved booking page's URL with its slug swapped for the page picked in
// the form, so the preview follows the dropdown before the notice is saved.
export const bookingPageUrl = (savedUrl, slug) =>
  savedUrl && slug ? savedUrl.replace(/[^/]+\/?$/, slug) : savedUrl || null

// What one patient would read, for the preview in the cancel modal.
export const appointmentValues = ({
  appointment,
  reason,
  clinicName,
  bookingUrl,
}) => {
  const { patient, appointmentType, doctor, startsAt } = appointment
  const intakeForm = patient.intakeForm || {}

  return {
    'First name': patient.firstName,
    Appointment: appointmentType?.title || '',
    Day: safeFormat(startsAt, 'EEE, MMM d', ''),
    Time: safeFormat(startsAt, 'h:mm a', ''),
    Doctor: doctor ? `Dr. ${doctor.fullName}` : '',
    'Clinic name': clinicName || '',
    Reason: reason,
    'Health history link':
      intakeForm.status === HEALTH_HISTORY_DONE || !intakeForm.url
        ? ''
        : `Please complete your health history: ${intakeForm.url}`,
    'Booking link': bookingUrl || '',
  }
}

// A template as the patient reads it, or null when it can't be read as plain
// text.
export const renderNotice = (template, values) => {
  const plain = parse(template)
  return plain === null ? null : fill(plain, values)
}
